import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import {
  Body,
  Link,
  SectionHeader as BonsaiSectionHeader,
} from '@grnhse/bonsai/lib/components/typography';
import {
  MultiCheckboxGroup,
  TextInput,
} from '@grnhse/bonsai/lib/components/forms';
import { COLORS } from '@grnhse/bonsai/lib/core/colors';
import { PronounOption } from '../candidate_pronouns_field';

const t = withLocalePrefix('availability.candidate_pronouns');

interface Props {
  defaultPronounsOptions: string[];
  pronouns: PronounOption[];
  isMobile: boolean;
}

export const BrandedPronounsField = (props: Props) => {
  const [showTips, setShowTips] = useState(false);
  const [pronouns, setPronouns] = useState(props.pronouns);
  const [selfDescribeValue, setSelfDescribeValue] = useState(
    pronouns.find((p) => p.isSelfDescribe)?.value || ''
  );

  const toggleTips = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowTips(!showTips);
  };

  const examples = () => t('tips.examples').split('\n').filter(Boolean);

  const options = useMemo(() => {
    return [...props.defaultPronounsOptions, 'self_describe'].map((option) => ({
      label: t(`options.${option}`),
      name: `pronouns_attributes[${option}]`,
      value: option,
    }));
  }, [props.defaultPronounsOptions]);

  const values = useMemo(() => {
    const pronounValues = pronouns.map((p) => p.value);

    if (pronouns.some((p) => p.isSelfDescribe)) {
      pronounValues.push('self_describe');
    }

    return pronounValues;
  }, [pronouns]);

  const isChecked = (option: string) => {
    if (option === 'self_describe') {
      return pronouns.some((p) => p.isSelfDescribe);
    } else {
      return pronouns.some((p) => p.value === option);
    }
  };

  const handleCheckboxChange = (newValues: Array<string | number>) => {
    setPronouns(
      newValues.map((value) => ({
        value: value as string,
        isSelfDescribe: value === 'self_describe',
      }))
    );
  };

  return (
    <section>
      <BonsaiSectionHeader>
        {t('header')} <Optional>{t('optional')}</Optional>
      </BonsaiSectionHeader>
      <div style={{ marginTop: '10px' }}>
        <Body>{t('subheader')}</Body>
        <Collapsible id="pronoun_tips" isOpen={showTips}>
          <div style={{ marginTop: '10px' }}>
            <Body medium>{t('tips.header')}</Body>
            <Body>{t('tips.body')}</Body>
          </div>
          <div style={{ marginTop: '10px' }}>
            <Body medium>{t('tips.example_header')}</Body>
            <Body>
              {examples().map((example) => (
                <>
                  {example}
                  <br />
                </>
              ))}
            </Body>
          </div>
        </Collapsible>
        <div style={{ marginTop: '10px' }}>
          <Link href="#" onClick={toggleTips}>
            {showTips ? t('tips.hide') : t('tips_trigger')}
          </Link>
        </div>
      </div>
      <CheckboxWrapper>
        <MultiCheckboxGroup
          onChange={handleCheckboxChange}
          values={values}
          options={options}
        ></MultiCheckboxGroup>
        {isChecked('self_describe') && (
          <div style={{ marginTop: '16px' }}>
            <TextInput
              name={'pronouns_attributes[self_describe_input]'}
              onChange={(value) => setSelfDescribeValue(value)}
              value={selfDescribeValue}
              placeholder={t('options.placeholder')}
            />
          </div>
        )}
      </CheckboxWrapper>
    </section>
  );
};

const CheckboxWrapper = styled.div`
  width: 400px;
  max-width: 100%;
  margin-top: 10px;
`;

const Optional = styled.span`
  color: ${COLORS.mediumGray};
`;
